import React from 'react';
import { TrendingUp, TrendingDown, Minus, Loader, AlertCircle } from 'lucide-react';
import './PersonalBestSection.css';

const PersonalBestSection = ({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="section-loading">
        <div className="section-loading-spinner" />
        <p>Loading personal best comparison...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-error">
        <AlertCircle className="section-error-icon" />
        <p className="section-error-message">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="section-no-data">
        <p>No personal best data available yet. Complete more attempts to see comparisons.</p>
      </div>
    );
  }

  // Safe number formatting helper
  const safeFixed = (value, decimals = 2) => {
    return (value != null && !isNaN(value)) ? Number(value).toFixed(decimals) : 'N/A';
  };

  // Format metric names for display
  const formatMetricName = (key) => {
    // Ensure key is a string
    if (typeof key !== 'string') {
      return String(key);
    }

    const nameMap = {
      jump_height_cm: 'Jump Height',
      rotation_speed_rev_s: 'Rotation Speed',
      rotation_count: 'Rotation Count',
      takeoff_velocity_m_s: 'Takeoff Velocity',
      landing_stability_score: 'Landing Stability',
      isu_score: 'ISU Score',
      air_time_s: 'Air Time'
    };
    return nameMap[key] || key.replace(/_/g, ' ');
  };

  // Format metric values with units
  const formatMetricValue = (key, value) => {
    if (value === null || value === undefined) return 'N/A';
    
    const unitMap = {
      jump_height_cm: 'cm',
      rotation_speed_rev_s: 'rev/s',
      rotation_count: '',
      takeoff_velocity_m_s: 'm/s',
      landing_stability_score: '/100',
      isu_score: '',
      air_time_s: 's'
    };
    
    const unit = unitMap[key] || '';
    const formatted = (value != null && !isNaN(value)) ? Number(value).toFixed(2) : 'N/A';
    return `${formatted}${unit ? ' ' + unit : ''}`;
  };

  // Get trend icon
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving':
        return <TrendingUp className="trend-icon trend-improving" />;
      case 'declining':
        return <TrendingDown className="trend-icon trend-declining" />;
      default:
        return <Minus className="trend-icon trend-stable" />;
    }
  };

  const improvements = data.improvements || {};
  const regressions = data.regressions || {};
  const hasImprovements = Object.keys(improvements).length > 0;
  const hasRegressions = Object.keys(regressions).length > 0;

  return (
    <div className="personal-best-section">
      {/* Overall Trend */}
      <div className="trend-badge">
        {getTrendIcon(data.overall_trend)}
        <span className={`trend-text trend-${data.overall_trend}`}>
          {data.overall_trend === 'improving' ? 'Improving' :
           data.overall_trend === 'declining' ? 'Declining' : 'Stable'}
        </span>
      </div>

      {/* Motivation Message */}
      {data.motivation_message && (
        <div className="motivation-message">
          <p>{data.motivation_message}</p>
        </div>
      )}

      {/* Improvements List */}
      {hasImprovements && (
        <div className="metrics-section">
          <h5 className="metrics-title improvements-title">
            ✓ Improvements
          </h5>
          <div className="metrics-list">
            {Object.entries(improvements).map(([metric, change]) => (
              <div key={metric} className="metric-item improvement-item">
                <div className="metric-header">
                  <span className="metric-name">{formatMetricName(metric)}</span>
                  <span className="metric-change improvement-change">
                    +{safeFixed(change.change_pct, 1)}%
                  </span>
                </div>
                <div className="metric-values">
                  <span className="metric-value-label">Current:</span>
                  <span className="metric-value">{formatMetricValue(metric, change.current)}</span>
                  <span className="metric-divider">→</span>
                  <span className="metric-value-label">Best:</span>
                  <span className="metric-value">{formatMetricValue(metric, change.previous_best)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Regressions List */}
      {hasRegressions && (
        <div className="metrics-section">
          <h5 className="metrics-title regressions-title">
            ⚠ Areas to Focus
          </h5>
          <div className="metrics-list">
            {Object.entries(regressions).map(([metric, change]) => (
              <div key={metric} className="metric-item regression-item">
                <div className="metric-header">
                  <span className="metric-name">{formatMetricName(metric)}</span>
                  <span className="metric-change regression-change">
                    {safeFixed(change.change_pct, 1)}%
                  </span>
                </div>
                <div className="metric-values">
                  <span className="metric-value-label">Current:</span>
                  <span className="metric-value">{formatMetricValue(metric, change.current)}</span>
                  <span className="metric-divider">→</span>
                  <span className="metric-value-label">Best:</span>
                  <span className="metric-value">{formatMetricValue(metric, change.previous_best)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No changes message */}
      {!hasImprovements && !hasRegressions && (
        <div className="no-changes">
          <p>Performance is consistent with your personal best. Keep up the good work!</p>
        </div>
      )}
    </div>
  );
};

export default PersonalBestSection;
